import { useI18n, useT } from './i18n'

/**
 * A place name in the reader's language.
 *
 * cities.name and city_points.name hold one English string each, so a fully
 * translated app still said "Jeddah" and "The gateway by the sea" in the
 * middle of an Urdu screen. These names are few and stable, so they are
 * translated through the same catalogue as everything else rather than
 * through a jsonb column and a locale on every endpoint that returns a
 * place.
 *
 * The key is the English name slugged. A place added later with no
 * translation falls back to exactly the English it shows today, which is why
 * this is safe to call on every name in the app without auditing the data
 * first.
 */
export function placeKey(english: string): string {
  const slug = english
    .normalize('NFKD')
    .replace(/[^\w\s-]/g, '')       // Ta’if -> Taif, an-Nabawi keeps its dash
    .trim()
    .split(/[\s-]+/)
    .map((w, i) => (i === 0 ? w.toLowerCase() : w[0]!.toUpperCase() + w.slice(1).toLowerCase()))
    .join('')
  return `place.${slug}`
}

/** `const place = usePlaceName(); place('Al Haram')` */
export function usePlaceName(): (english: string | null | undefined) => string {
  const msg = useT()
  return (english) => {
    if (!english) return ''
    return msg(placeKey(english), { fallback: english })
  }
}

/** The line under a city card, same rules. */
export function useTagline(): (city: string, tagline: string | null | undefined) => string {
  const msg = useT()
  return (city, tagline) => {
    // The key is tried even with no tagline on the row: Taif has none and
    // was falling through to "Mecca Region · SA" beside five written ones.
    const slug = placeKey(city).replace('place.', '')
    return msg(`place.tagline.${slug}`, { fallback: tagline ?? '' })
  }
}

/**
 * Two places and the arrow between them, as one string.
 *
 * It was a hard-coded `\u2192` in twenty places, which in Urdu and Arabic points
 * back at the origin. Swapping the glyph is not enough on its own: an arrow
 * is bidi-neutral, so "Karachi \u2190 Lahore" sits between two Latin names, takes
 * THEIR direction, and the whole phrase is laid out left to right inside the
 * right-to-left line - origin on the left, arrow aimed at it. The isolates
 * stop the names lending the arrow their direction, so the phrase is ordered
 * by the paragraph: origin at the start of the line, arrow pointing at the
 * destination. Correct whether the names are translated or still in Latin.
 */
export function useRoute(): (from: string | null | undefined, to: string | null | undefined) => string {
  const place = usePlaceName()
  const { dir } = useI18n()
  const arrow = dir === 'rtl' ? '\u2190' : '\u2192'
  return (from, to) => `\u2068${place(from)}\u2069 ${arrow} \u2068${place(to)}\u2069`
}

/** Just the glyph, for a canvas, which cannot call a hook. */
export function useRouteArrow(): string {
  return useI18n().dir === 'rtl' ? '\u2190' : '\u2192'
}
